/**
 * Validation for broker registration and the listing/project editors.
 */
export const validateFullName = (value) => {
    if (!value || !value.trim()) return "Full name is required";
    if (value.trim().length < 3) return "Name must be at least 3 characters";
    return null;
};

export const validateCompanyName = (value) => {
    if (!value || !value.trim()) return "Company name is required";
    return null;
};

export const validateReraNumber = (value) => {
    if (!value || !value.trim()) return null;
    // Karnataka: PRM/KA/RERA/1251/446/AG/171126/001234 (agents), PRM/KA/RERA/1251/309/PR/... (projects).
    if (!/^[A-Z0-9][A-Z0-9/\- ]{5,}$/i.test(value.trim())) return "Enter the RERA number as on your certificate";
    return null;
};

export const validateMobile = (value) => {
    if (!value || !value.trim()) return "Mobile number is required";
    if (!/^\d{10}$/.test(value.trim())) return "Enter a valid 10-digit mobile number";
    return null;
};

export const validateEmail = (value) => {
    if (!value || !value.trim()) return "Email address is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) return "Enter a valid email address";
    return null;
};

export const validateOfficeAddress = (value) => {
    if (!value || !value.trim()) return "Office address is required";
    if (value.trim().length < 10) return "Please enter a complete address";
    return null;
};

export const validateOperatingAreas = (areas) => {
    if (!areas || areas.length === 0) return "Select at least one operating area";
    return null;
};

export const validatePropertyTypes = (types) => {
    if (!types || types.length === 0) return "Select at least one property type";
    return null;
};

export const validateNumberOfProperties = (value) => {
    if (!value && value !== 0) return null;
    if (isNaN(value) || Number(value) < 0) return "Enter a valid number";
    return null;
};

export const validateTerms = (agreed) => {
    if (!agreed) return "You must agree to the Terms & Conditions";
    return null;
};

export const validateOtp = (otp, expectedLength = 6) => {
    if (!otp || otp.trim() === "") return "OTP is required";
    if (otp.trim().length !== expectedLength) return `Enter the ${expectedLength}-digit OTP`;
    return null;
};

export const validateRegistrationForm = ({ form, otpVerified, selectedAreas, selectedTypes, agreed }) => {
    const errors = {};

    const nameErr = validateFullName(form.fullName);
    if (nameErr) errors.fullName = nameErr;

    const companyErr = validateCompanyName(form.companyName);
    if (companyErr) errors.companyName = companyErr;

    const reraErr = validateReraNumber(form.reraNumber);
    if (reraErr) errors.reraNumber = reraErr;

    const mobileErr = validateMobile(form.mobile);
    if (mobileErr) errors.mobile = mobileErr;

    if (!otpVerified) errors.otp = "Please verify your mobile number via OTP";

    const emailErr = validateEmail(form.email);
    if (emailErr) errors.email = emailErr;

    const addressErr = validateOfficeAddress(form.officeAddress);
    if (addressErr) errors.officeAddress = addressErr;

    const areasErr = validateOperatingAreas(selectedAreas);
    if (areasErr) errors.operatingAreas = areasErr;

    const typesErr = validatePropertyTypes(selectedTypes);
    if (typesErr) errors.propertyTypes = typesErr;

    const termsErr = validateTerms(agreed);
    if (termsErr) errors.terms = termsErr;

    return errors;
};

/** "12.97, 77.59" or a Google Maps link (…/@12.97,77.59,… or …?q=12.97,77.59) → [lat, lng]; null if neither. */
export const parseCoordinates = (value) => {
    if (!value || !String(value).trim()) return null;
    const text = String(value);
    const match = text.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/)
        || text.match(/[?&](?:q|query|ll)=(-?\d+\.\d+),\s*(-?\d+\.\d+)/)
        || text.match(/^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/);
    if (!match) return null;
    const lat = Number(match[1]);
    const lng = Number(match[2]);
    return Math.abs(lat) <= 90 && Math.abs(lng) <= 180 ? [lat, lng] : null;
};

const wholeNumber = (v, max) => v === "" || v === null || v === undefined
    || (Number.isInteger(Number(v)) && Number(v) >= 0 && Number(v) <= max);

/** Errors for a listing (or unit) form, keyed by field. `submit` also requires what review needs (a photo). */
export const validateListing = (form, {submit = false, unit = false} = {}) => {
    const errors = {};
    if (!unit) {
        if (!form.title || form.title.trim().length < 5) errors.title = "Give it a title of at least 5 characters";
        if (!form.location || form.location.trim().length < 2) errors.location = "Enter the area or locality";
        if (form.coordinates && !parseCoordinates(form.coordinates)) {
            errors.coordinates = "Paste a Google Maps link or “latitude, longitude”";
        }
        const reraError = validateReraNumber(form.reraNumber);
        if (reraError) errors.reraNumber = reraError;
        if (form.videoUrl && !/^https?:\/\//i.test(form.videoUrl.trim())) errors.videoUrl = "Enter a full link starting with https://";
        if (submit && !(form.mainImages && form.mainImages.length)) errors.mainImages = "Add at least one photo before submitting";
    }
    const price = Number(form.price);
    if (!form.price || !(price > 0)) errors.price = "Enter the price in rupees";
    else if (price < 100000 || price > 1e10) errors.price = "That price looks wrong — enter the full amount in rupees";
    if (!wholeNumber(form.bedrooms, 20)) errors.bedrooms = "Enter a whole number";
    if (!wholeNumber(form.bathrooms, 20)) errors.bathrooms = "Enter a whole number";
    if (!wholeNumber(form.sqft, 1000000)) errors.sqft = "Enter the area in square feet";
    if (unit && !form.bedrooms && !form.bhk && !form.title) errors.bedrooms = "Enter the bedrooms (or a name for this unit type)";
    return errors;
};

/** Errors for a project form; `submit` also checks what review needs. */
export const validateProject = (form, {submit = false, unitCount = 0} = {}) => {
    const errors = {};
    if (!form.name || form.name.trim().length < 2) errors.name = "Enter the project name";
    if (!form.location || form.location.trim().length < 2) errors.location = "Enter the area or locality";
    if (form.coordinates && !parseCoordinates(form.coordinates)) errors.coordinates = "Paste a Google Maps link or “latitude, longitude”";
    const reraError = validateReraNumber(form.reraNumber);
    if (reraError) errors.reraNumber = reraError;
    if (form.videoUrl && !/^https?:\/\//i.test(form.videoUrl.trim())) errors.videoUrl = "Enter a full link starting with https://";
    if (submit && !(form.photos && form.photos.length)) errors.photos = "Add a cover photo before submitting";
    if (submit && unitCount === 0) errors.units = "Add at least one unit type with a price before submitting";
    return errors;
};
